import React, { useState, useRef, useEffect } from "react";
import "./InputDropDown.css";

export interface InputDropDownProps {
  /**
   * Texto placeholder cuando no hay selección
   */
  placeholder?: string;

  /**
   * Lista de opciones para mostrar en el dropdown
   */
  options: Array<{
    value: string;
    name: string;
    disabled?: boolean;
  }>;

  /**
   * Valor seleccionado actualmente
   */
  value?: string;

  /**
   * Callback que se ejecuta al seleccionar una opción
   */
  onChange?: (value: string) => void;

  /**
   * Tamaño del componente
   */
  size?: "sm" | "md";

  /**
   * Si el componente tiene un error
   */
  error?: boolean;

  /**
   * Mensaje de error para mostrar
   */
  errorMessage?: string;

  /**
   * Si el componente está deshabilitado
   */
  disabled?: boolean;

  /**
   * Clase CSS adicional
   */
  className?: string;
}

/**
 * Componente InputDropDown para selección de opciones en un menú desplegable
 */
export const InputDropDown: React.FC<InputDropDownProps> = ({
  placeholder = "Selecciona...",
  options = [],
  value,
  onChange,
  size = "md",
  error = false,
  errorMessage,
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Actualizar el estado interno cuando cambia el valor de prop
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Manejar clics fuera del dropdown para cerrarlo
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Manejar tecla Escape para cerrar el dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Manejar la selección de una opción
  const handleSelect = (option: { value: string; name: string; disabled?: boolean }) => {
    setSelectedValue(option.value);
    setIsOpen(false);
    if (onChange) {
      onChange(option.value);
    }
  };

  // Encontrar la etiqueta seleccionada para mostrar
  const selectedLabel =
    options.find((option) => option.value === selectedValue)?.name ||
    placeholder;

  // Construir clases CSS basadas en props
  const containerClasses = [
    "qida-input-dropdown",
    `qida-input-dropdown--${size}`,
    isOpen ? "qida-input-dropdown--open" : "",
    error ? "qida-input-dropdown--error" : "",
    disabled ? "qida-input-dropdown--disabled" : "",
    selectedValue ? "qida-input-dropdown--filled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputClasses = [
    "qida-input-dropdown__input",
    isOpen ? "qida-input-dropdown__input--open" : "",
    error ? "qida-input-dropdown__input--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const menuClasses = [
    "qida-input-dropdown__menu",
    `qida-input-dropdown__menu--${size}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={dropdownRef} className={containerClasses}>
      <div
        className={inputClasses}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        <div className="qida-input-dropdown__selected-value">
          {selectedLabel}
        </div>
        <div
          className={`qida-input-dropdown__icon ${isOpen ? "qida-input-dropdown__icon--open" : ""}`}
        >
          <svg
            width="15"
            height="8.5"
            viewBox="0 0 15 8.5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 1.5L7.5 7.5L13.5 1.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          className={menuClasses}
          role="listbox"
          aria-labelledby="dropdown-label"
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`qida-input-dropdown__item ${
                option.value === selectedValue
                  ? "qida-input-dropdown__item--selected"
                  : ""
              }`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={option.value === selectedValue}
            >
              {option.name}
            </div>
          ))}
        </div>
      )}

      {error && errorMessage && (
        <div className="qida-input-dropdown__error-message">{errorMessage}</div>
      )}
    </div>
  );
};

export default InputDropDown;
